import { get } from 'lodash';
import { NumberField, NumberFieldProps, useLocaleState, useRecordContext } from 'react-admin';

import {
  buildCurrencyOptions,
  convertCentsInUnits,
  convertMicroInUnits,
  LooseAutocomplete,
  TCurrencies,
} from '@sh4res/bo-common/utils';

type CurrencyFieldProps = Omit<
  NumberFieldProps,
  'variant' | 'currency' | 'precision' | 'data-testid'
> & {
  variant?: 'cents' | 'units' | 'micro';
  currency: LooseAutocomplete<TCurrencies>;
  precision?: number;
  source?: string;
  currencyDisplay?: 'code' | 'symbol';
  testId?: string;
};

export const CurrencyField = (props: CurrencyFieldProps) => {
  const { source, variant, currency, precision, currencyDisplay, testId, ...additionalProps } =
    props;

  const record = useRecordContext(props);
  const [locale] = useLocaleState();

  if (source === undefined) {
    return null;
  }

  const value = get(record, source);

  if (value === null || value === undefined) {
    return <NumberField data-testid={testId} {...additionalProps} source={source} />;
  }

  const convertValue = (): number => {
    if (variant === 'cents') {
      return convertCentsInUnits(value);
    }
    if (variant === 'micro') {
      return convertMicroInUnits(value);
    }
    return value as number;
  };

  const valueConverted: number = convertValue();

  const currencyFinal = get(record, currency, currency);
  const fieldOptions = currencyFinal
    ? buildCurrencyOptions(currencyFinal, precision, currencyDisplay)
    : undefined;

  return (
    <NumberField
      data-testid={testId}
      {...additionalProps}
      source={source}
      record={{ ...record, [source]: valueConverted }}
      locales={locale}
      options={fieldOptions}
    />
  );
};

CurrencyField.defaultProps = {
  currency: 'EUR',
  sortable: false,
  variant: 'cents',
  precision: 20,
};
